import {
  JourneyMapDraft,
  JourneyMapDraftItem,
  JourneyMapDraftLink,
  JourneyMapSectionDelimiter,
  JourneyMapSectionDelimiterDraft,
} from '../../types/journeyMap';
import { createDraftSectionDelimiter, makeTempId } from './builderFactory';

export const normalizeLinkDefaults = (
  linkType: JourneyMapDraftLink['link_type'] | undefined,
  existingConditionType?: JourneyMapDraftLink['condition_type'],
  existingConditionConfig?: Record<string, any>,
) => {
  const nextType = linkType || 'default';
  const nextConfig = { ...(existingConditionConfig || {}) };
  let nextConditionType: JourneyMapDraftLink['condition_type'] =
    existingConditionType || 'always';

  if (nextType === 'default') {
    nextConditionType = 'always';
  }

  if (nextType === 'optional') {
    nextConditionType = existingConditionType || 'always';
    if (!nextConfig.fallback_behavior) {
      nextConfig.fallback_behavior = 'allow_other_alternates';
    }
  }

  if (nextType === 'alternate') {
    nextConditionType = 'always';
    if (nextConfig.completion_equivalent === undefined) {
      nextConfig.completion_equivalent = true;
    }
    if (nextConfig.branch_group === undefined) {
      nextConfig.branch_group = '';
    }
  }

  if (nextType === 'conditional') {
    if (!existingConditionType || existingConditionType === 'always') {
      nextConditionType = 'rule';
    }
    if (nextConfig.branch_group === undefined) {
      nextConfig.branch_group = '';
    }
    if (nextConfig.fallback_behavior === undefined) {
      nextConfig.fallback_behavior = '';
    }
    if (nextConfig.lock_after_exhausted === undefined) {
      nextConfig.lock_after_exhausted = false;
    }
  }

  if (nextType === 'test_out') {
    nextConditionType = 'rule';
    if (nextConfig.branch_group === undefined) {
      nextConfig.branch_group = '';
    }
    if (nextConfig.max_attempts === undefined) {
      nextConfig.max_attempts = 2;
    }
    if (nextConfig.lock_after_exhausted === undefined) {
      nextConfig.lock_after_exhausted = true;
    }
    if (nextConfig.fallback_behavior === undefined) {
      nextConfig.fallback_behavior = 'default_path_only';
    }
  }

  if (nextType === 'fallback') {
    nextConditionType = 'always';
    if (nextConfig.fallback_behavior === undefined) {
      nextConfig.fallback_behavior = 'route_to_fallback_link';
    }
  }

  if (nextType === 'pass' || nextType === 'fail' || nextType === 'skip') {
    nextConditionType = 'always';
  }

  return {
    condition_type: nextConditionType,
    condition_config: nextConfig,
  };
};

export const normalizeSectionDelimiters = (
  delimiters?: JourneyMapSectionDelimiter[] | JourneyMapSectionDelimiterDraft[],
): JourneyMapSectionDelimiterDraft[] =>
  Array.isArray(delimiters)
    ? delimiters.map((delimiter) =>
      createDraftSectionDelimiter({
        ...delimiter,
        tempId:
          (delimiter as JourneyMapSectionDelimiterDraft).tempId ||
          makeTempId('delimiter'),
        isDirty: false,
        isDeleted: false,
      }),
    )
    : [];

const normalizeDraftItems = (items?: JourneyMapDraftItem[]): JourneyMapDraftItem[] => {
  if (!Array.isArray(items)) return [];

  return items.map((item) => {
    const metadata =
      item?.metadata && typeof item.metadata === 'object' ? item.metadata : {};

    return {
      ...item,
      position_x:
        item?.position_x ?? metadata?.position_x ?? 240,
      position_y:
        item?.position_y ?? metadata?.position_y ?? 140,
      metadata,
    };
  });
};

export const normalizeDraft = (draft: JourneyMapDraft): JourneyMapDraft => {
  const metadata =
    draft?.metadata && typeof draft.metadata === 'object' ? draft.metadata : {};

  return {
    ...draft,
    background_image_url:
      draft.background_image_url ||
      metadata.background_image_url ||
      '',
    items: normalizeDraftItems(draft.items),
    section_delimiters: normalizeSectionDelimiters(
      draft.section_delimiters || metadata.section_delimiters,
    ),
  };
};